'use client';

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface BlogRatingProps {
  blogSlug: string;
  currentLanguage: 'en' | 'de';
}

interface RatingStats {
  average_rating: number;
  total_votes: number;
}

// Generate or retrieve session ID for anonymous users
const getSessionId = (): string => {
  if (typeof window === 'undefined') return '';
  
  const STORAGE_KEY = 'blog_rating_session_id';
  let sessionId = localStorage.getItem(STORAGE_KEY);
  
  if (!sessionId) {
    // Generate unique session ID
    sessionId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(STORAGE_KEY, sessionId);
  }
  
  return sessionId;
};

export default function BlogRating({ blogSlug, currentLanguage }: BlogRatingProps) {
  const { user } = useAuth();
  const [stats, setStats] = useState<RatingStats | null>(null);
  const [userRating, setUserRating] = useState<number | null>(null);
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId] = useState<string>(getSessionId);

  const translations = {
    en: {
      title: 'Rate this article',
      averageRating: 'Average rating',
      votes: 'votes',
      vote: 'vote',
      yourRating: 'Your rating',
      clickToRate: 'Click to rate',
      thankYou: 'Thank you for rating!',
      errorLoading: 'Could not load ratings',
      errorSubmitting: 'Could not submit rating',
    },
    de: {
      title: 'Bewerten Sie diesen Artikel',
      averageRating: 'Durchschnittsbewertung',
      votes: 'Bewertungen',
      vote: 'Bewertung',
      yourRating: 'Ihre Bewertung',
      clickToRate: 'Klicken Sie zum Bewerten',
      thankYou: 'Vielen Dank für Ihre Bewertung!',
      errorLoading: 'Bewertungen konnten nicht geladen werden',
      errorSubmitting: 'Bewertung konnte nicht übermittelt werden',
    },
  };

  const t = translations[currentLanguage];

  // Fetch rating stats and user's rating
  useEffect(() => {
    fetchRatings();
  }, [blogSlug, user, sessionId]);

  const fetchRatings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch stats
      const { data: statsData, error: statsError } = await supabase
        .from('blog_rating_stats')
        .select('average_rating, total_votes')
        .eq('blog_slug', blogSlug)
        .single();

      if (statsError && statsError.code !== 'PGRST116') {
        throw statsError;
      }

      setStats(statsData || { average_rating: 0, total_votes: 0 });

      // Fetch user's rating (authenticated or anonymous)
      let query = supabase
        .from('blog_ratings')
        .select('rating')
        .eq('blog_slug', blogSlug);

      if (user) {
        // Authenticated user
        query = query.eq('user_id', user.id);
      } else {
        // Anonymous user - use session ID
        query = query.eq('session_id', sessionId);
      }

      const { data: userRatingData, error: userRatingError } = await query.single();

      if (userRatingError && userRatingError.code !== 'PGRST116') {
        throw userRatingError;
      }

      setUserRating(userRatingData?.rating || null);
    } catch (err: any) {
      console.error('Error fetching ratings:', err);
      setError(t.errorLoading);
    } finally {
      setLoading(false);
    }
  };

  const handleRating = async (rating: number) => {
    try {
      setSubmitting(true);
      setError(null);

      const ratingData: any = {
        blog_slug: blogSlug,
        rating: rating,
        updated_at: new Date().toISOString(),
      };

      if (user) {
        // Authenticated user
        ratingData.user_id = user.id;
        ratingData.session_id = null;
      } else {
        // Anonymous user
        ratingData.user_id = null;
        ratingData.session_id = sessionId;
      }

      // Check if rating already exists
      let existingQuery = supabase
        .from('blog_ratings')
        .select('id')
        .eq('blog_slug', blogSlug);

      if (user) {
        existingQuery = existingQuery.eq('user_id', user.id);
      } else {
        existingQuery = existingQuery.eq('session_id', sessionId);
      }

      const { data: existingRating } = await existingQuery.single();

      let submitError;
      if (existingRating) {
        // Update existing rating
        const { error } = await supabase
          .from('blog_ratings')
          .update({ rating, updated_at: new Date().toISOString() })
          .eq('id', existingRating.id);
        submitError = error;
      } else {
        // Insert new rating
        const { error } = await supabase
          .from('blog_ratings')
          .insert(ratingData);
        submitError = error;
      }

      if (submitError) {
        throw submitError;
      }

      setUserRating(rating);
      // Refresh stats after rating
      await fetchRatings();
    } catch (err: any) {
      console.error('Error submitting rating:', err);
      setError(t.errorSubmitting);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating: number, interactive: boolean = false) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = interactive
            ? (hoveredStar !== null ? star <= hoveredStar : star <= (userRating || 0))
            : star <= Math.round(rating);

          return (
            <button
              key={star}
              type="button"
              disabled={submitting || !interactive}
              onClick={() => interactive && handleRating(star)}
              onMouseEnter={() => interactive && setHoveredStar(star)}
              onMouseLeave={() => interactive && setHoveredStar(null)}
              className={`transition-all duration-fast ${
                interactive
                  ? 'cursor-pointer hover:scale-110 transform'
                  : 'cursor-default'
              } ${submitting ? 'opacity-50' : ''}`}
              aria-label={`${star} star${star > 1 ? 's' : ''}`}
            >
              <svg
                className={`w-8 h-8 transition-colors duration-fast ${
                  isFilled ? 'text-accent fill-current' : 'text-foreground-muted'
                }`}
                fill={isFilled ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="1.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
                />
              </svg>
            </button>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <section className="mt-12 pt-8 border-t border-border">
        <div className="flex items-center justify-center py-8">
          <div className="animate-pulse text-foreground-secondary">{t.title}...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-12 pt-8 border-t border-border">
      <h2 className="font-heading font-bold text-2xl text-foreground-light mb-6">{t.title}</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Average Rating Display */}
      {stats && stats.total_votes > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            {renderStars(stats.average_rating, false)}
            <span className="text-foreground-light font-semibold text-lg">
              {stats.average_rating.toFixed(2)}
            </span>
          </div>
          <p className="text-foreground-secondary text-sm">
            {t.averageRating} • {stats.total_votes} {stats.total_votes === 1 ? t.vote : t.votes}
          </p>
        </div>
      )}

      {/* User Rating Section - Now available to everyone */}
      <div>
        <p className="text-foreground-secondary text-sm mb-3">
          {userRating ? t.yourRating : t.clickToRate}
        </p>
        {renderStars(0, true)}
        {userRating && (
          <p className="text-accent text-sm mt-2">{t.thankYou}</p>
        )}
      </div>
    </section>
  );
}